/* ==========================================================================
   VoxTune — Experience Manager
   The single centralized runtime every section talks to. Avoids isolated
   scripts: sections register modules here and communicate via the event bus.
   Loaded once, early, in the <head> (tiny, no dependencies).

   Public surface:
     VoxTune.on(evt, fn) / once / off / emit(evt, payload)
     VoxTune.state            — shared reactive-ish store (get/set/subscribe)
     VoxTune.module(name, fn) — lazy module registration
     VoxTune.use(name)        — resolve/boot a module on demand
     VoxTune.track(evt, data) — standardized analytics dispatch
     VoxTune.money(cents)     — format using shop money format
   ========================================================================== */
(function (w) {
  'use strict';
  if (w.VoxTune) return; // singleton guard

  /* ---- event bus -------------------------------------------------------- */
  var listeners = Object.create(null);

  function on(evt, fn) {
    (listeners[evt] || (listeners[evt] = [])).push(fn);
    return function off() { offFn(evt, fn); };
  }
  function once(evt, fn) {
    var wrap = function (p) { offFn(evt, wrap); fn(p); };
    return on(evt, wrap);
  }
  function offFn(evt, fn) {
    var a = listeners[evt]; if (!a) return;
    var i = a.indexOf(fn); if (i > -1) a.splice(i, 1);
  }
  function emit(evt, payload) {
    var a = listeners[evt]; if (a) a.slice().forEach(function (fn) {
      try { fn(payload); } catch (e) { console.error('[VoxTune] listener for "' + evt + '" threw:', e); }
    });
    // wildcard listeners
    var star = listeners['*']; if (star) star.slice().forEach(function (fn) {
      try { fn({ type: evt, payload: payload }); } catch (e) { /* noop */ }
    });
    return payload;
  }

  /* ---- shared state store ---------------------------------------------- */
  var data = Object.create(null);
  var subs = Object.create(null);
  var state = {
    get: function (k) { return data[k]; },
    set: function (k, v) {
      var prev = data[k];
      if (prev === v) return v;
      data[k] = v;
      (subs[k] || []).slice().forEach(function (fn) { try { fn(v, prev); } catch (e) {} });
      emit('state:change', { key: k, value: v, prev: prev });
      return v;
    },
    update: function (k, fn) { return state.set(k, fn(data[k])); },
    subscribe: function (k, fn) { (subs[k] || (subs[k] = [])).push(fn); return function () {
      var a = subs[k]; var i = a ? a.indexOf(fn) : -1; if (i > -1) a.splice(i, 1);
    }; },
    all: function () { return data; }
  };

  /* ---- lazy module registry -------------------------------------------- */
  var registry = Object.create(null); // name -> factory
  var instances = Object.create(null); // name -> instance

  function module(name, factory) { registry[name] = factory; return VoxTune; }
  function use(name) {
    if (instances[name]) return instances[name];
    var f = registry[name];
    if (!f) { console.warn('[VoxTune] module not registered:', name); return null; }
    instances[name] = f(VoxTune) || {};
    emit('module:ready', { name: name });
    return instances[name];
  }
  function has(name) { return !!(instances[name] || registry[name]); }

  /* ---- money formatting (mirrors Shopify money_format) ----------------- */
  function money(cents) {
    var fmt = (VoxTune.shop && VoxTune.shop.moneyFormat) || '${{amount}}';
    var amount = (Number(cents || 0) / 100);
    function withCommas(num, decimals) {
      var n = (Number(num) || 0).toFixed(decimals == null ? 2 : decimals);
      var parts = n.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return withCommas(amount, 2);
        case 'amount_no_decimals': return withCommas(amount, 0);
        case 'amount_with_comma_separator': return withCommas(amount, 2).replace(/,/g, ' ').replace('.', ',');
        case 'amount_no_decimals_with_comma_separator': return withCommas(amount, 0).replace(/,/g, '.');
        default: return withCommas(amount, 2);
      }
    });
  }

  /* ---- standardized analytics dispatch --------------------------------- */
  /* Sections call VoxTune.track('AddToCart', {...}); the analytics module
     (loaded conditionally) fans this out to Meta/TikTok/GA4/GTM/Clarity. */
  var STANDARD_EVENTS = [
    'ProductViewed', 'AudioPlayed', 'ProgramSelected', 'AddToCart',
    'UpsellAccepted', 'UpsellDeclined', 'CheckoutStarted'
  ];
  function track(evt, dataObj) {
    var payload = dataObj || {};
    emit('track', { event: evt, data: payload });
    emit('track:' + evt, payload);
    // GTM dataLayer is always safe to push to if present
    if (w.dataLayer && typeof w.dataLayer.push === 'function') {
      w.dataLayer.push(Object.assign({ event: 'voxtune.' + evt }, payload));
    }
    return payload;
  }

  /* ---- tiny DOM-ready + section hook ----------------------------------- */
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  var VoxTune = {
    on: on, once: once, off: offFn, emit: emit,
    state: state,
    module: module, use: use, has: has,
    track: track, STANDARD_EVENTS: STANDARD_EVENTS,
    money: money,
    ready: ready,
    version: '0.1.0'
  };

  w.VoxTune = VoxTune;

  // Drop the no-js guard as early as possible for progressive enhancement.
  ready(function () {
    document.documentElement.classList.remove('no-js');
    document.documentElement.classList.add('js');
    emit('ready');
  });
})(window);
